import { BenefitChangeRequestService } from '@account/services/benefit-plus/benefit-change-request.service';
import { AfterContentChecked, Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BadgeOptions } from '@app/interfaces';
import { BenefitsChangeRequestBanner } from '@app/modules/benefits/components';
import { AuthService, FileHelperService, NotifyService } from '@app/services';
import { HelloSignEmbedService, HelloSignResponse } from '@app/services/hello-sign-embed.service';
import { BenefitChangeRequest } from '@models/benefits/benefit-change-request.model';
import { BenefitPlan } from '@models/benefits/benefit-plan.model';
import { Benefit } from '@models/benefits/benefit.model';
import { SignatureRequest } from '@models/documents/signature-request.model';
import { BenefitPlan as EmployeeBenefitPlan } from '@models/employee/benefits/benefit-plan.model';
import { Employee } from '@models/employee/employee.model';

//app-benefits-change-request-banner
@Component({
    templateUrl: './benefits-show.template.html',
    styleUrls: ['./benefits-show.style.scss'],
    providers: [HelloSignEmbedService],
})
export class BenefitsShowView implements AfterContentChecked {
    @ViewChild(BenefitsChangeRequestBanner) changeRequestBanner: BenefitsChangeRequestBanner;

    isLoading = true;
    employee: Employee;
    employeeBenefitPlan: EmployeeBenefitPlan;
    badgeOptions: BadgeOptions;
    //don't show the button by default. we don't want duplicated change requests or to confuse the user
    hasPendingChangeRequests = true;
    shouldShowCard = false;

    constructor(
        public auth: AuthService,
        public file: FileHelperService,
        protected route: ActivatedRoute,
        protected notify: NotifyService,
        private router: Router,
        private helloSign: HelloSignEmbedService,
        private benefitChangeRequestService: BenefitChangeRequestService
    ) {
        this.isLoading = true;
        this.employee = this.route.snapshot.data.models.get('employee');
        this.resolve();
    }

    resolve(): void {
        EmployeeBenefitPlan.param('employee', this.employee.id)
            .with([
                'benefitPlan',
                'benefitPlan.benefitPolicy',
                'benefitDivision',
                'benefitPlan.file',
                'benefitPlan.benefitProvider',
                'benefitPlan.benefits',
                'benefitPlan.benefits.benefitProvider',
                'benefitPlan.benefits.file',
                'benefitPlan.benefits.benefitCategory',
                'benefitPlan.benefits.benefitDetails',
                'benefitPlan.benefits.benefitDetails.benefitType',
                'overrides',
                'changeRequests',
            ])
            .all()
            .then(([benefitPlans]) => {
                if (!benefitPlans.length) {
                    this.employeeBenefitPlan = new EmployeeBenefitPlan({
                        employeeId: this.employee.id,
                    });

                    this.isLoading = false;

                    return;
                }
                // One to many however we only support one to one in the UI
                this.employeeBenefitPlan = benefitPlans[0];
                this.shouldShowCard = this.employeeBenefitPlan?.shouldShowCard();

                // If any pending CRs exist, don't let user make a new one
                this.benefitChangeRequestService
                    .getPendingChangeRequests(this.employeeBenefitPlan.benefitPlanId)
                    .then((allChangeRequests: BenefitChangeRequest[]) => {
                        this.hasPendingChangeRequests = !!allChangeRequests?.length;
                    });
                this.isLoading = false;
            });
    }

    ngAfterContentChecked(): void {
        if (!this.employeeBenefitPlan) {
            return;
        }

        const enrollmentFormDueDays = this.employeeBenefitPlan.getEnrollmentFormDueDaysLeft();
        const label =
            enrollmentFormDueDays > 0
                ? {
                      key: 'account.benefits.daysLeft',
                      params: {
                          days: enrollmentFormDueDays,
                      },
                  }
                : 'account.benefits.overdue';

        this.badgeOptions = {
            label: label,
            type: 'warning',
        };
    }

    getBenefitPlanEffectiveAt(): Date {
        return this.employeeBenefitPlan.overrideEffectiveAt ?? this.employeeBenefitPlan.effectiveAt;
    }

    onDownloadPlan(plan: BenefitPlan): void {
        this.file.save(plan.fileId, plan.name, '.pdf');
    }

    onDownloadBenefit(benefitPlan: BenefitPlan, benefit: Benefit): void {
        const fileName = benefitPlan.name + (benefit.benefitCategory ? ' - ' + benefit.benefitCategory.name : '');

        this.file.save(benefit.fileId, fileName, '.pdf');
    }

    /**
     * Create PDF based Change Request for Benefits enrollment.
     */
    async onChangeRequest(): Promise<void> {
        this.isLoading = true;
        const newChangeRequest = await this.createDraftChangeRequestWithSignature();
        if (!newChangeRequest.createdAt) {
            this.notify.error('account.benefits.couldNotCreateChangeRequest');
            this.isLoading = false;
            return;
        }

        const signingUrl = await this.getSigningUrlOfBenefitChangeRequest(newChangeRequest);
        if (!signingUrl) {
            this.notify.error('account.benefits.couldNotRetrieveSigningUrl');
            this.isLoading = false;
            return;
        }

        this.isLoading = false;
        this.hasPendingChangeRequests = true;
        // Open signature request via modal
        this.helloSign
            .open(signingUrl, null, true)
            .then(async (response: HelloSignResponse) => {
                if (response.closedByUser) {
                    //means user did not finish filling document (cancelled or closed pdf)
                    this.deleteRecentlyCreatedBenefitChangeRequest(newChangeRequest);
                    return;
                }

                setTimeout(() => {
                    //refresh banners
                    this.changeRequestBanner.getAllChangeRequestsBasedOnAcknowledgement();
                }, 20000); //wait for Hello Sign API calls to finish
            })
            .catch(() => {
                this.notify.error('account.benefits.couldNotProcessDocumentSignature');
                return;
            });
    }

    //If the entity was not created then createdAt of the returned entity will be null/undefined
    private async createDraftChangeRequestWithSignature(): Promise<BenefitChangeRequest> {
        const newChangeRequest: BenefitChangeRequest = new BenefitChangeRequest();
        try {
            await newChangeRequest
                .param('company', this.auth.company.id)
                .param('benefitPlan', this.employeeBenefitPlan.benefitPlanId)
                .save();
        } catch (err: any) {
            this.notify.error(err.message);
            return new BenefitChangeRequest();
        }

        if (!newChangeRequest.employeeDocument) {
            return new BenefitChangeRequest();
        }

        return await BenefitChangeRequest.param('company', this.auth.company.id)
            .param('benefitPlan', this.employeeBenefitPlan.benefitPlanId)
            .with('employeeDocument')
            .find(newChangeRequest.id);
    }

    private async getSigningUrlOfBenefitChangeRequest(changeRequest: BenefitChangeRequest): Promise<string | null> {
        if (!changeRequest.employeeDocument) {
            return null;
        }
        const source = changeRequest.employeeDocument.source as SignatureRequest;
        const signatureRequest = await SignatureRequest.param('signingTemplate', source.signingTemplateId).find(
            source.id
        );
        return signatureRequest.signingUrl ?? null;
    }

    private deleteRecentlyCreatedBenefitChangeRequest(changeRequest: BenefitChangeRequest): void {
        this.isLoading = true;
        changeRequest
            .param('company', this.auth.company.id)
            .param('benefitPlan', this.employeeBenefitPlan.benefitPlanId)
            .delete()
            .then(() => {
                this.isLoading = false;
                this.hasPendingChangeRequests = false;
            })
            .catch((err) => {
                this.notify.error(err.message || 'account.benefits.couldNotDeleteChangeRequest');
            });
    }
}
